import { useState } from 'react';
import { useAppStore } from '../../store/useAppStore.js';

export function ChecklistPanel({ checklist = [], appId, onUpdate }) {
  const templates = useAppStore((s) => s.templates);
  const [newItem, setNewItem] = useState('');

  function addItem() {
    if (!newItem.trim()) return;
    const item = { id: crypto.randomUUID(), text: newItem.trim(), completed: false };
    onUpdate([...checklist, item]);
    setNewItem('');
  }

  function toggleItem(id) {
    onUpdate(checklist.map((i) => (i.id === id ? { ...i, completed: !i.completed } : i)));
  }

  function removeItem(id) {
    onUpdate(checklist.filter((i) => i.id !== id));
  }

  function applyTemplate(templateId) {
    const template = templates.find((t) => t.templateId === templateId);
    if (!template) return;
    const newItems = template.items.map((i) => ({ id: crypto.randomUUID(), text: i.text, completed: false }));
    onUpdate([...checklist, ...newItems]);
  }

  function moveItem(id, direction) {
    const idx = checklist.findIndex((i) => i.id === id);
    if (idx < 0) return;
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= checklist.length) return;
    const updated = [...checklist];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    onUpdate(updated);
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-slate-600">Checklist</label>
        {templates.length > 0 && (
          <select
            onChange={(e) => e.target.value && applyTemplate(e.target.value)}
            value=""
            className="text-xs rounded border border-slate-300 px-1 py-0.5"
          >
            <option value="">Apply template…</option>
            {templates.map((t) => (
              <option key={t.templateId} value={t.templateId}>{t.templateName}</option>
            ))}
          </select>
        )}
      </div>

      <ul className="space-y-1">
        {checklist.map((item, idx) => (
          <li key={item.id} className="flex items-center gap-2 text-sm">
            <div className="flex flex-col">
              <button
                type="button"
                onClick={() => moveItem(item.id, -1)}
                disabled={idx === 0}
                className="text-[10px] leading-none text-slate-400 hover:text-slate-600 disabled:opacity-20"
              >▲</button>
              <button
                type="button"
                onClick={() => moveItem(item.id, 1)}
                disabled={idx === checklist.length - 1}
                className="text-[10px] leading-none text-slate-400 hover:text-slate-600 disabled:opacity-20"
              >▼</button>
            </div>
            <input
              type="checkbox"
              checked={item.completed}
              onChange={() => toggleItem(item.id)}
              className="rounded border-slate-300"
            />
            <span className={item.completed ? 'line-through text-slate-400' : 'text-slate-700'}>{item.text}</span>
            <button onClick={() => removeItem(item.id)} className="ml-auto text-xs text-slate-400 hover:text-destructive-600">&times;</button>
          </li>
        ))}
      </ul>

      <div className="flex gap-1">
        <input
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addItem())}
          placeholder="Add item"
          className="flex-1 rounded border border-slate-300 px-2 py-1 text-base focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
        <button type="button" onClick={addItem} className="rounded bg-slate-100 px-2 py-1 text-xs hover:bg-slate-200">Add</button>
      </div>
    </div>
  );
}
